import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight, faImage } from '@fortawesome/free-solid-svg-icons'

type PhotoGalleryFrameProps = {
  images: string[]
  index: number
  onIndexChange: (index: number) => void
  alt?: string
  /** wide = 4:3 panel; square = fixed thumb */
  variant?: 'wide' | 'square'
  emptyLabel?: string
  className?: string
  children?: React.ReactNode
}

export function photoGallerySafeIndex(index: number, length: number): number {
  if (!length) return 0
  return ((index % length) + length) % length
}

/** Shared photo frame with on-image prev/next overlays (add + view parity). */
export const PhotoGalleryFrame: React.FC<PhotoGalleryFrameProps> = ({
  images,
  index,
  onIndexChange,
  alt = 'Photo',
  variant = 'wide',
  emptyLabel,
  className = '',
  children,
}) => {
  const safeIndex = photoGallerySafeIndex(index, images.length)
  const preview = images[safeIndex]
  const frameClass =
    variant === 'square'
      ? 'w-40 h-40'
      : 'w-full aspect-[4/3] max-h-52'

  const step = (delta: number) => {
    if (images.length < 2) return
    onIndexChange(photoGallerySafeIndex(safeIndex + delta, images.length))
  }

  return (
    <div
      className={`${frameClass} relative rounded-box bg-base-200 border border-base-300 flex items-center justify-center overflow-hidden ${className}`}
    >
      {preview ? (
        <img src={preview} alt={alt} className="w-full h-full object-contain" />
      ) : (
        <div className="flex flex-col items-center gap-2 px-3 text-base-content/40">
          <FontAwesomeIcon icon={faImage} className="text-4xl" />
          {emptyLabel ? <span className="text-xs text-center">{emptyLabel}</span> : null}
        </div>
      )}

      {images.length > 1 ? (
        <>
          <button
            type="button"
            className="btn btn-circle btn-sm absolute left-2 top-1/2 z-20 -translate-y-1/2 bg-base-100/90 border-base-300 shadow"
            onClick={(e) => {
              e.stopPropagation()
              step(-1)
            }}
            aria-label="Previous photo"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button
            type="button"
            className="btn btn-circle btn-sm absolute right-2 top-1/2 z-20 -translate-y-1/2 bg-base-100/90 border-base-300 shadow"
            onClick={(e) => {
              e.stopPropagation()
              step(1)
            }}
            aria-label="Next photo"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
          <span className="pointer-events-none absolute bottom-2 left-1/2 z-20 -translate-x-1/2 badge badge-sm badge-neutral shadow">
            {safeIndex + 1} / {images.length}
          </span>
        </>
      ) : null}

      {children}
    </div>
  )
}

export default PhotoGalleryFrame
